import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../services/api';
import { MonthlyReport } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const MonthlyReportTable: React.FC = () => {
  const { t } = useLanguage();
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [taxRate, setTaxRate] = useState<number>(0);

  const fetchTaxRate = useCallback(async () => {
    try {
      const response = await reportsApi.getTaxRate();
      setTaxRate(response.data.rate);
    } catch (error) {
      console.error('Error fetching tax rate:', error);
      // Fallback to default tax rate if fetch fails
      setTaxRate(0.045);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getMonthlyReport(selectedYear, selectedMonth);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    fetchTaxRate();
  }, [fetchTaxRate]);

  // Generate year options (from 2020 to current year + 1)
  const years = Array.from({ length: new Date().getFullYear() - 2019 + 1 }, (_, i) => 2020 + i);
  const months = [
    { value: 1, label: t('january') },
    { value: 2, label: t('february') },
    { value: 3, label: t('march') },
    { value: 4, label: t('april') },
    { value: 5, label: t('may') },
    { value: 6, label: t('june') },
    { value: 7, label: t('july') },
    { value: 8, label: t('august') },
    { value: 9, label: t('september') },
    { value: 10, label: t('october') },
    { value: 11, label: t('november') },
    { value: 12, label: t('december') }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t('monthly.financial.report')}</h5>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : report ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-dark">
                <tr>
                  <th>{t('vehicle')}</th>
                  <th>{t('trips')}</th>
                  <th>{t('revenue')}</th>
                  <th>{t('driver.salary')}</th>
                  <th>{t('fuel.cost')}</th>
                  <th>{t('repair.cost')}</th>
                  <th>{t('load.unload.fee')}</th>
                  <th>{t('toll.fee')}</th>
                  <th>{t('rm2.fee')}</th>
                  <th>{t('total.costs')}</th>
                  <th>{t('tax')} ({(taxRate * 100).toFixed(1)}%)</th>
                  <th>{t('profit')}</th>
                </tr>
              </thead>
              <tbody>
                {report.vehicles.map((vehicle, index) => (
                  <tr key={vehicle.licensePlate}>
                    <td className="fw-bold">{vehicle.licensePlate}</td>
                    <td className="text-center">{vehicle.tripCount}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.revenue)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.driverSalary)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.fuelCosts)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.repairCosts)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.loadUnloadFees)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.tollFees)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.rM2Fees)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.totalCosts)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.tax)}</td>
                    <td className={`text-end fw-bold ${vehicle.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatVietnamCurrency(vehicle.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-secondary">
                <tr className="fw-bold">
                  <td>{t('total')}</td>
                  <td className="text-center">{report.totals.totalTrips}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalRevenue)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalDriverSalary)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalFuelCosts)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalRepairCosts)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalLoadUnloadFees)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalTollFees)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalRM2Fees)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalCosts)}</td>
                  <td className="text-end">{formatVietnamCurrency(report.totals.totalTax)}</td>
                  <td className={`text-end ${report.totals.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatVietnamCurrency(report.totals.totalProfit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-muted text-center">{t('no.data.available')} {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</p>
        )}
      </div>
    </div>
  );
};

export default MonthlyReportTable;